/**
 * Cookie Service
 * Provides methods to set, get, and delete cookies with TypeScript support
 */

interface CookieOptions {
  expires?: number | Date; // Number of days or specific Date
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean; // Note: This cannot be set from JavaScript
}

// Auth cookie names that should be shared across subdomains
const AUTH_COOKIE_NAMES = ['token', 'refreshToken'];

class CookieService {
  /**
   * Get the domain for auth cookies (shared across subdomains in production)
   * Returns undefined for localhost, .rentbhai.com for production
   */
  private getAuthCookieDomain(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    
    const hostname = window.location.hostname;
    
    // Don't set domain for localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return undefined;
    }
    
    // For rentbhai.com and its subdomains, use the parent domain
    if (hostname.endsWith('app.rentbhai.com')) {
      return '.rentbhai.com';
    }
    
    // For other domains, don't set a specific domain
    return undefined;
  }

  /**
   * Set a cookie with optional expiry and other options
   * @param name - Cookie name
   * @param value - Cookie value (will be stringified if object)
   * @param options - Cookie options including expiry
   */
  set(name: string, value: any, options: CookieOptions = {}): void {
    if (!name) {
      console.error('Cookie name is required');
      return;
    }

    // Convert value to string if it's an object
    let cookieValue: string;
    if (typeof value === 'object') {
      try {
        cookieValue = JSON.stringify(value);
      } catch (error) {
        console.error('Failed to stringify cookie value:', error);
        return;
      }
    } else {
      cookieValue = String(value);
    }

    // Encode the value to handle special characters
    cookieValue = encodeURIComponent(cookieValue);

    // Build cookie string
    let cookieString = `${name}=${cookieValue}`;

    // Handle expiry
    if (options.expires) {
      let expiryDate: Date;

      if (typeof options.expires === 'number') {
        // If number, treat as days from now
        expiryDate = new Date();
        expiryDate.setTime(expiryDate.getTime() + (options.expires * 24 * 60 * 60 * 1000));
      } else if (options.expires instanceof Date) {
        // If Date object, use directly
        expiryDate = options.expires;
      } else {
        console.error('Invalid expires option. Must be number of days or Date object');
        return;
      }

      cookieString += `; expires=${expiryDate.toUTCString()}`;
    }

    // Add path (default to root)
    cookieString += `; path=${options.path || '/'}`;

    // Determine domain - use shared domain for auth cookies if not explicitly set
    let domain = options.domain;
    if (!domain && AUTH_COOKIE_NAMES.includes(name)) {
      domain = this.getAuthCookieDomain();
    }
    
    // Add domain if specified
    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    // Add secure flag - required for cross-subdomain cookies in production
    if (options.secure || (domain && domain.startsWith('.'))) {
      cookieString += '; secure';
    }

    // Add sameSite attribute - use 'lax' for auth cookies to allow cross-site navigation
    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    } else if (AUTH_COOKIE_NAMES.includes(name)) {
      cookieString += '; samesite=lax';
    }

    // Set the cookie
    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   * @param name - Cookie name
   * @returns Cookie value (parsed if JSON) or null if not found
   */
  get(name: string): any {
    if (!name) {
      console.error('Cookie name is required');
      return null;
    }

    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      // Remove leading spaces
      cookie = cookie.trim();

      if (cookie.indexOf(nameEQ) === 0) {
        let value = cookie.substring(nameEQ.length);

        // Decode the value
        try {
          value = decodeURIComponent(value);

          // Try to parse as JSON if it looks like JSON
          if ((value.startsWith('{') && value.endsWith('}')) ||
              (value.startsWith('[') && value.endsWith(']'))) {
            try {
              return JSON.parse(value);
            } catch {
              // If parsing fails, return as string
              return value;
            }
          }

          return value;
        } catch (error) {
          console.error('Failed to decode cookie value:', error);
          return null;
        }
      }
    }

    return null;
  }

  /**
   * Delete a cookie by name
   * @param name - Cookie name
   * @param options - Optional path and domain (must match the cookie's original settings)
   */
  delete(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void {
    if (!name) {
      console.error('Cookie name is required');
      return;
    }

    // Set cookie with expired date to delete it
    this.set(name, '', {
      ...options,
      expires: new Date(0) // Set to past date to expire immediately
    });
  }

  /**
   * Get all cookies as an object
   * @returns Object with all cookies
   */
  getAll(): Record<string, any> {
    const cookies: Record<string, any> = {};
    const cookieStrings = document.cookie.split(';');

    for (let cookie of cookieStrings) {
      cookie = cookie.trim();

      if (cookie) {
        const [name, ...valueParts] = cookie.split('=');
        const value = valueParts.join('='); // Handle values with = signs

        if (name && value) {
          try {
            const decodedValue = decodeURIComponent(value);

            // Try to parse as JSON
            if ((decodedValue.startsWith('{') && decodedValue.endsWith('}')) ||
                (decodedValue.startsWith('[') && decodedValue.endsWith(']'))) {
              try {
                cookies[name] = JSON.parse(decodedValue);
              } catch {
                cookies[name] = decodedValue;
              }
            } else {
              cookies[name] = decodedValue;
            }
          } catch {
            cookies[name] = value;
          }
        }
      }
    }

    return cookies;
  }

  /**
   * Check if a cookie exists
   * @param name - Cookie name
   * @returns True if cookie exists, false otherwise
   */
  exists(name: string): boolean {
    return this.get(name) !== null;
  }

  /**
   * Clear all cookies (for current path and domain)
   * Note: This only clears cookies accessible to JavaScript
   */
  clearAll(): void {
    const cookies = this.getAll();

    for (const name in cookies) {
      this.delete(name);
      // Also try to delete with common paths
      this.delete(name, { path: '/' });
      this.delete(name, { path: window.location.pathname });
    }
  }

  /**
   * Set a session cookie (expires when browser closes)
   * @param name - Cookie name
   * @param value - Cookie value
   * @param options - Optional cookie options (without expires)
   */
  setSession(name: string, value: any, options: Omit<CookieOptions, 'expires'> = {}): void {
    // Don't set expires for session cookie
    const { expires, ...sessionOptions } = options as CookieOptions;
    this.set(name, value, sessionOptions);
  }

  /**
   * Set a cookie that expires in minutes
   * @param name - Cookie name
   * @param value - Cookie value
   * @param minutes - Number of minutes until expiry
   * @param options - Optional additional cookie options
   */
  setWithMinutes(name: string, value: any, minutes: number, options: Omit<CookieOptions, 'expires'> = {}): void {
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + (minutes * 60 * 1000));

    this.set(name, value, {
      ...options,
      expires: expiryDate
    });
  }

  /**
   * Set a cookie that expires in hours
   * @param name - Cookie name
   * @param value - Cookie value
   * @param hours - Number of hours until expiry
   * @param options - Optional additional cookie options
   */
  setWithHours(name: string, value: any, hours: number, options: Omit<CookieOptions, 'expires'> = {}): void {
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + (hours * 60 * 60 * 1000));

    this.set(name, value, {
      ...options,
      expires: expiryDate
    });
  }

  /**
   * Get remaining time until cookie expires (in milliseconds)
   * Note: This is not possible with JavaScript as expiry info is not accessible
   * This is a placeholder that returns -1
   */
  getExpiryTime(_name: string): number {
    // JavaScript cannot access cookie expiry time
    console.warn('Cookie expiry time is not accessible via JavaScript');
    return -1;
  }
}

// Export singleton instance
const cookieService = new CookieService();
export default cookieService;

// Also export the class for testing or custom instances
export { CookieService };
export type { CookieOptions };